import React, { useState, useEffect, useMemo, CSSProperties, useCallback } from "react";
import { useTranslation } from "react-i18next";
import notifyStyles from "./NotifySurface.module.css";
import { Formik, Form, useFormikContext, FormikHelpers, FastField, Field } from "formik";
import * as Yup from "yup";
import activeRadio from "../../assets/images/radio_active.png";
import inactiveRadio from "../../assets/images/radio_inactive.png";
import { getProducts, getAdminInfo } from "../../api";
import { getActivities } from "../../api/activity";
import { COMPANY_ADDONS, hasAddon } from "../../utils/addons";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import fr from "date-fns/locale/fr";
import enUS from "date-fns/locale/en-US";
import { ProductDropdownOption } from "../../types/screensTypes";
import { useGlobalState } from "../../context/useGlobalState";
import { createNotification } from "../../api/notification";
import Button from "../../components/ui/Button";
import { FileInput } from "../../components/common/FileInput";
import { uploadFileToS3Complete } from "../../api/s3";
import ImageList from "../../components/images/ImageList";
import {
    TextField,
    DropdownField,
    RadioField,
    NumberField,
    TimePickerField,
    DatePickerField,
} from "../../components/common/FormFields";
import FormGroup from "../../components/common/FormGroup";
import ImageSpecsBadges from "../../components/common/ImageSpecsBadges";
import { useImageValidation } from "../../hooks/useImageValidation";
import { IMAGE_FORMAT, IMAGE_MAX_SIZE_200_KB } from "../../utils/image";
import { Tooltip } from "@mui/material";
import NotificationStatusModal from "./NotificationCreationModal";
import bellIcon from "../../assets/images/bell.svg";
import closeCrossIcon from "../../assets/images/close-cross.svg";
import NotificationCompactPreview from "./NotificationCompactPreview";
import previewStyles from "./NotificationPreview.module.css";

interface NotificationFormValues {
    notificationType: "urgent" | "information" | "redirection";
    notificationScope: "allApp" | "track";
    selectedProduct: string;
    notificationStartDate: Date | null;
    notificationEndDate: Date | null;
    conditionType: string;
    conditionValue: string;
    conditionStartHour: Date | null;
    conditionEndHour: Date | null;
    redirectionLinkType: "external" | "inapp" | "";
    linkValue: string;
    hotinfoLinkValue: string;
    shortTextValue: string;
    textValue: string;
    logoNotification: string | null;
    contentLanguage: string;
    cityId: string;
}

// Notification Preview Component
const NotificationPreview = () => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { values } = useFormikContext<NotificationFormValues>();

    const { notificationType, logoNotification, linkValue, shortTextValue, textValue } = values;

    const showModal = notificationType === "information" || (notificationType === "redirection" && !!linkValue);

    return (
        <div className={notifyStyles.notificationPreviewContainer}>
            <NotificationCompactPreview
                shortText={shortTextValue}
                fullText={textValue}
                logoUrl={logoNotification}
                notificationType={notificationType}
            />

            {/* Modal-style preview for redirection notifications with a link */}
            {showModal && (
                <div className={`${previewStyles.group} ${previewStyles.groupExpanded}`}>
                    <h2>{t("expandedNotificationPreview")}</h2>
                    <div className={previewStyles.modalPreview}>
                        <img src={closeCrossIcon} alt="" className={previewStyles.modalClose} />
                        <img src={bellIcon} alt="" className={previewStyles.modalBell} />
                        <div className={previewStyles.modalTitle}>{shortTextValue || t("notificationTitle")}</div>
                        <div className={previewStyles.modalMessage}>{textValue || t("notificationContent")}</div>
                        {notificationType === "redirection" && !!linkValue && (
                            <div className={previewStyles.modalButtons}>
                                <button className={previewStyles.modalBtnPrimary}>{t("learnMore")}</button>
                                <button className={previewStyles.modalBtnSecondary}>{t("noThanks")}</button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const fixedButtonContainerStyle: CSSProperties = {
    position: "fixed",
    bottom: "0",
    left: "0",
    right: "0",
    display: "flex",
    justifyContent: "center",
    zIndex: 1000,
    padding: "20px",
    backgroundColor: "rgba(255, 255, 255)",
    boxShadow: "0 -2px 10px rgba(0, 0, 0, 0.1)",
    borderTop: "1px solid #eee",
};

const NotificationForm = () => {
    const { t: rawT, i18n } = useTranslation();
    const t = rawT as any;
    const dateLocale = i18n.language?.startsWith("fr") ? fr : enUS;
    const { data } = useGlobalState();
    const [products, setProducts] = useState<ProductDropdownOption[]>([]);
    const [productsRedirect, setProductsRedirect] = useState<ProductDropdownOption[]>([]);
    const [companyCities, setCompanyCities] = useState<ProductDropdownOption[]>([]);
    const [citiesLoading, setCitiesLoading] = useState(true);
    const [productsLoading, setProductsLoading] = useState(true);
    // Map of trackId → cityId so we can auto-fill the notification's cityId when
    // scope === "track" (the track already implies its city).
    const [trackCityMap, setTrackCityMap] = useState<Record<string, string>>({});

    const { values, setFieldValue } = useFormikContext<NotificationFormValues>();

    const { notificationType, notificationScope, conditionType, redirectionLinkType, logoNotification } = values;

    const imageValidation = useImageValidation({
        type: "notificationLogo",
        imageUrl: logoNotification || undefined,
    });

    // Derived state based on form values
    const isUrgent = useMemo(() => values.notificationType === "urgent", [values.notificationType]);
    const isRedirection = useMemo(() => values.notificationType === "redirection", [values.notificationType]);
    const isInformation = useMemo(() => values.notificationType === "information", [values.notificationType]);
    const isSingleTour = useMemo(() => values.notificationScope === "track", [values.notificationScope]);

    useEffect(() => {
        const token = localStorage.getItem("token");
        if (!token) {
            console.error("No token found");
            return;
        }

        // Load admin info (and therefore cities) independently of products,
        // so the city picker doesn't wait for the (slow) product list.
        const loadAdminAndCities = async () => {
            const adminInfo = await getAdminInfo({ token }).catch(() => null);

            const cities = (adminInfo?.cmpany as any)?.cities ?? [];
            const cityOptions: ProductDropdownOption[] = cities.map(
                (c: { id: string; internalName?: string | null }) => ({
                    value: c.id,
                    label: c.internalName || c.id,
                }),
            );
            setCompanyCities(cityOptions);
            if (cityOptions.length === 1) {
                setFieldValue("cityId", cityOptions[0].value);
            }
            setCitiesLoading(false);

            // Activities depend on the company addon, so they live with this load.
            if (adminInfo?.cmpany && hasAddon(adminInfo.cmpany, COMPANY_ADDONS.ACTIVITIES)) {
                try {
                    const activitiesData = await getActivities({ token });
                    if (activitiesData?.products) {
                        const activitiesList = activitiesData.products
                            .filter((a) => a.isActive !== false)
                            .map((a) => ({ value: a.id, label: a.name }));
                        setProductsRedirect((prev) => [...prev, ...activitiesList]);
                    }
                } catch (err) {
                    console.error("Failed to fetch activities for notifications", err);
                }
            }
        };

        const loadProducts = async () => {
            const productsData = await getProducts({ token }).catch((err) => {
                console.error("Failed to fetch products", err);
                return null;
            });
            if (productsData) {
                setProducts(
                    productsData.map((p: { trackId: string; name: string }) => ({
                        value: p.trackId,
                        label: p.name,
                    })),
                );
                setProductsRedirect((prev) => [
                    ...productsData.map((p: { id: string; name: string }) => ({
                        value: p.id,
                        label: p.name,
                    })),
                    ...prev,
                ]);
                setTrackCityMap(
                    productsData.reduce((acc: Record<string, string>, p: { trackId: string; cityId?: string }) => {
                        if (p.trackId && p.cityId && p.cityId !== "NA") {
                            acc[p.trackId] = p.cityId;
                        }
                        return acc;
                    }, {}),
                );
            }
            setProductsLoading(false);
        };

        loadAdminAndCities();
        loadProducts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Auto-fill cityId from the selected track when scope is "track".
    // This keeps every notification row in DB with a concrete cityId, even though
    // the CMS doesn't show a city picker for track-scoped notifications.
    useEffect(() => {
        if (notificationScope !== "track") return;
        const trackCity = values.selectedProduct ? trackCityMap[values.selectedProduct] : undefined;
        if (trackCity && trackCity !== values.cityId) {
            setFieldValue("cityId", trackCity);
        }
    }, [notificationScope, values.selectedProduct, trackCityMap, values.cityId, setFieldValue]);

    useEffect(() => {
        switch (notificationType) {
            case "urgent":
                setFieldValue("notificationScope", "allApp");
                setFieldValue("redirectionLinkType", "");
                setFieldValue("linkValue", "");
                break;
            case "information":
                setFieldValue("redirectionLinkType", "");
                setFieldValue("linkValue", "");
                break;
            case "redirection":
                setFieldValue("linkValue", "");
                break;
        }

        if (notificationScope === "allApp") {
            setFieldValue("selectedProduct", "");
            setFieldValue("redirectionLinkType", "");
            setFieldValue("linkValue", "");
            // Clear "audios" condition type if switching to "allApp" since it's only available for specific experience
            if (conditionType === "audios") {
                setFieldValue("conditionType", "");
                setFieldValue("conditionValue", "");
            }
        } else if (notificationScope === "track") {
            setFieldValue("redirectionLinkType", "");
            setFieldValue("linkValue", "");
        }

        if (conditionType) {
            setFieldValue("conditionValue", "");
            setFieldValue("conditionStartHour", null);
            setFieldValue("conditionEndHour", null);
        }
    }, [notificationType, notificationScope, conditionType, setFieldValue]);

    const handleFileSelected = useCallback(
        async (files: File | File[]) => {
            const file = Array.isArray(files) ? files[0] : files;
            if (file) {
                const token = localStorage.getItem("token");
                if (!token) {
                    console.error("No authentication token found");
                    return;
                }

                await imageValidation.validateImage(file);

                uploadFileToS3Complete(
                    {
                        file,
                        isPublic: true, // Public files for general uploads
                        isFolder: false, // No folder structure for general uploads
                    },
                    token,
                )
                    .then((result) => {
                        setFieldValue("logoNotification", result);
                    })
                    .catch(console.error);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [setFieldValue, imageValidation.validateImage],
    );

    const setPicturesFromList = useCallback(
        (pictures: string[]) => {
            setFieldValue("logoNotification", pictures[0] || null);
        },
        [setFieldValue],
    );

    return (
        <Form>
            <div>
                <h1 className={notifyStyles.sidebarSettingsTitle} style={{ marginTop: "12px" }}>
                    {t("generalSettings")}
                </h1>
                <FormGroup
                    title={t("chooseNotificationType")}
                    subtitle={t("chooseNotificationTypeSub")}
                    className={notifyStyles.stepDescription}
                >
                    <Field
                        name="notificationType"
                        value="urgent"
                        label={t("urgentNotification")}
                        subtitle={t("urgentNotificationSubtitle")}
                        activeImage={activeRadio}
                        inactiveImage={inactiveRadio}
                        component={RadioField}
                    />
                    <Field
                        name="notificationType"
                        value="information"
                        label={t("informationNotification")}
                        subtitle={t("informationNotificationSubtitle")}
                        activeImage={activeRadio}
                        inactiveImage={inactiveRadio}
                        component={RadioField}
                    />
                    <Field
                        name="notificationType"
                        value="redirection"
                        label={t("purchaseNotification")}
                        subtitle={t("purchaseNotificationSubtitle")}
                        activeImage={activeRadio}
                        inactiveImage={inactiveRadio}
                        component={RadioField}
                    />
                </FormGroup>
            </div>

            {(isRedirection || isInformation || isUrgent) && (
                <>
                    {/* Scope Selection */}
                    <FormGroup
                        title={t("scopeNotification")}
                        subtitle={t("scopeNotificationSubtitle")}
                        className={notifyStyles.stepDescription}
                    >
                        <Field
                            name="notificationScope"
                            value="allApp"
                            label={t("allApp")}
                            subtitle={t("allAppSubtitle")}
                            activeImage={activeRadio}
                            inactiveImage={inactiveRadio}
                            component={RadioField}
                        />
                        <Field
                            name="notificationScope"
                            value="track"
                            label={t("singleTour")}
                            subtitle={t("singleTourSubtitle")}
                            activeImage={activeRadio}
                            inactiveImage={inactiveRadio}
                            disabled={isUrgent}
                            tooltip={isUrgent ? t("singleTourDisabled") : null}
                            component={RadioField}
                        />
                        {isSingleTour && (
                            <div style={{ marginTop: 10 }}>
                                <Field
                                    name="selectedProduct"
                                    options={products}
                                    placeholder={productsLoading ? t("loading") : t("all_products")}
                                    loading={productsLoading}
                                    disabled={productsLoading}
                                    component={DropdownField}
                                />
                            </div>
                        )}
                    </FormGroup>

                    {/* Location picker — only for global (allApp) notifications.
                        Single-tour notifications inherit their location from the track. */}
                    {notificationScope === "allApp" && (citiesLoading || companyCities.length > 1) && (
                        <FormGroup
                            title={t("location")}
                            subtitle={t("notificationLocationSubtitle")}
                            className={notifyStyles.stepDescription}
                            required
                        >
                            <Field
                                name="cityId"
                                options={companyCities}
                                placeholder={citiesLoading ? t("loading") : t("selectALocation")}
                                loading={citiesLoading}
                                disabled={citiesLoading}
                                component={DropdownField}
                            />
                        </FormGroup>
                    )}

                    {/* Notification Period */}
                    <FormGroup
                        title={t("notificationPeriod")}
                        tooltip={
                            t("notificationPeriodTooltip") || "The period during which the notification will be active"
                        }
                        className={notifyStyles.stepDescription}
                        required={true}
                    >
                        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateLocale}>
                            <div className={notifyStyles.datesRow}>
                                <FastField
                                    name="notificationStartDate"
                                    label={t("startDate")}
                                    placeholder={t("startDate")}
                                    errorMessage={t("invalidDate")}
                                    component={DatePickerField}
                                />
                                <FastField
                                    name="notificationEndDate"
                                    label={t("endDate")}
                                    placeholder={t("endDate")}
                                    minDate={values.notificationStartDate}
                                    errorMessage={t("invalidDate")}
                                    component={DatePickerField}
                                />
                            </div>
                        </LocalizationProvider>
                    </FormGroup>

                    {/* Conditions */}
                    {/* Conditions: only show when NOT urgent */}
                    {!isUrgent && (
                        <FormGroup title={t("conditionsNotification")} className={notifyStyles.stepDescription}>
                            <Field
                                name="conditionType"
                                options={[
                                    { value: "hour", label: t("hour") },
                                    { value: "timeSpent", label: t("time_spent") },
                                    // "Audios listened" only available for specific experience (track), not for all app
                                    ...(isSingleTour ? [{ value: "audios", label: t("audios_listened") }] : []),
                                ]}
                                placeholder={t("noValue")}
                                component={DropdownField}
                            />
                            {conditionType === "hour" && (
                                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateLocale}>
                                    <div className={notifyStyles.datesRow}>
                                        <Field
                                            name="conditionStartHour"
                                            label={t("startHour")}
                                            placeholder={t("startHour")}
                                            errorMessage={t("invalidTime")}
                                            component={TimePickerField}
                                        />

                                        <FastField
                                            name="conditionEndHour"
                                            label={t("endHour")}
                                            placeholder={t("endHour")}
                                            minTime={values.conditionStartHour}
                                            errorMessage={t("invalidTime")}
                                            component={TimePickerField}
                                        />
                                    </div>
                                </LocalizationProvider>
                            )}
                            {(conditionType === "audios" || conditionType === "timeSpent") && (
                                <FastField
                                    name="conditionValue"
                                    placeholder={t("selectNumber")}
                                    component={NumberField}
                                />
                            )}
                        </FormGroup>
                    )}

                    {isRedirection && (
                        <FormGroup title={t("selectLinkType")} className={notifyStyles.stepDescription}>
                            <Field
                                name="redirectionLinkType"
                                value="external"
                                label={t("externalLink")}
                                subtitle={t("externalLinkSubtitle")}
                                activeImage={activeRadio}
                                inactiveImage={inactiveRadio}
                                component={RadioField}
                            />
                            <Field
                                name="redirectionLinkType"
                                value="inapp"
                                label={t("inAppLink")}
                                subtitle={t("inAppLinkSubtitle")}
                                activeImage={activeRadio}
                                inactiveImage={inactiveRadio}
                                component={RadioField}
                            />

                            <div style={{ marginTop: 10 }}>
                                {redirectionLinkType === "external" && (
                                    <FastField
                                        name="linkValue"
                                        placeholder={t("provideLinkUrl")}
                                        size="medium"
                                        component={TextField}
                                    />
                                )}
                                {redirectionLinkType === "inapp" && (
                                    <FastField
                                        name="linkValue"
                                        options={productsRedirect}
                                        placeholder={productsLoading ? t("loading") : t("selectExperience")}
                                        loading={productsLoading}
                                        disabled={productsLoading}
                                        component={DropdownField}
                                    />
                                )}
                            </div>
                        </FormGroup>
                    )}

                    {/* Content & Redirection Link Type */}
                    <h1 className={`${notifyStyles.sidebarSettingsTitle} ${notifyStyles.sidebarSettingsTitleGap}`}>
                        {t("contentSettings")}
                    </h1>

                    <FormGroup
                        title={t("Language")}
                        subtitle={t("selectLanguageNotification")}
                        className={notifyStyles.stepDescription}
                        required
                    >
                        <Field
                            name="contentLanguage"
                            options={data.languagesAI}
                            placeholder={t("selectALanguage")}
                            component={DropdownField}
                        />
                    </FormGroup>

                    <FormGroup
                        title={t("uploadYourNotificationLogo")}
                        subtitle={t("dragYourLogoIntoTheAreaBelowOrSelectUploadLogo")}
                        className={notifyStyles.stepDescription}
                    >
                        <ImageSpecsBadges spec="notificationLogo" warnings={imageValidation.warnings} />
                        <div className={notifyStyles.fileUpload}>
                            <FileInput
                                onFileSelected={(files) => {
                                    handleFileSelected(files);
                                }}
                                showButton={true}
                                buttonVariant="outline"
                                buttonText={t("add")}
                                acceptedFileTypes={IMAGE_FORMAT}
                                maxSize={IMAGE_MAX_SIZE_200_KB}
                                maxSizeLabel={t("imageMaxSize200kb")}
                            />
                        </div>

                        {logoNotification && logoNotification?.length > 0 && (
                            <div className={notifyStyles.logoGallery}>
                                <ImageList
                                    pictures={logoNotification ? [logoNotification] : []}
                                    setPictures={setPicturesFromList}
                                />
                            </div>
                        )}

                        <FormGroup
                            name="shortTextValue"
                            title={t("shortText")}
                            tooltip={t("doNotTranslateTooltip")}
                            className={notifyStyles.stepDescription}
                            style={{ marginTop: 20 }}
                            required
                        >
                            <div className={notifyStyles.fieldCol}>
                                <FastField
                                    name="shortTextValue"
                                    placeholder={t("typeYourTextHere")}
                                    size="medium"
                                    component={TextField}
                                />
                            </div>
                        </FormGroup>

                        <FormGroup
                            name="textValue"
                            title={t("longText")}
                            tooltip={t("doNotTranslateTooltip")}
                            style={{ marginTop: 20 }}
                            required
                        >
                            <div className={notifyStyles.fieldCol}>
                                <FastField
                                    name="textValue"
                                    placeholder={t("typeYourTextHere")}
                                    component={TextField}
                                    size="big-with-border"
                                    numberOfLines={6}
                                />
                            </div>
                        </FormGroup>
                    </FormGroup>
                </>
            )}
        </Form>
    );
};

interface NewNotificationsProps {
    setActiveTab: (tab: "newNotification" | "activeNotifications") => void;
}

const NewNotifications: React.FC<NewNotificationsProps> = ({ setActiveTab }) => {
    const { t: rawT, i18n } = useTranslation();
    const t = rawT as any;

    const [status, setStatus] = useState<"waiting" | "success" | "error">("waiting");
    const [modalOpen, setModalOpen] = useState(false);

    // Validation schema
    const validationSchema = Yup.object({
        // Required and must match one of the valid types
        notificationType: Yup.mixed<"urgent" | "information" | "redirection">()
            .oneOf(["urgent", "information", "redirection"])
            .required(t("required")),
        notificationScope: Yup.mixed<"allApp" | "track">().oneOf(["allApp", "track"]).required(t("required")),
        // Required only when scope is "track"
        selectedProduct: Yup.string().when("notificationScope", {
            is: "track",
            then: (schema) => schema.required(t("productRequired")),
            otherwise: (schema) => schema.notRequired(),
        }),
        // Dates must be provided, and end >= start
        notificationStartDate: Yup.date().nullable().required(t("startDateRequired")),
        notificationEndDate: Yup.date()
            .nullable()
            .required(t("endDateRequired"))
            .min(Yup.ref("notificationStartDate"), t("endDateAfterStartDate") || "End date must be after start date"),
        // Optional conditions
        conditionType: Yup.string(),
        // Conditionally value require a number if condition is selected
        conditionValue: Yup.number()
            .typeError(t("valueMustBeNumber") || "Must be a number")
            .when("conditionType", {
                is: (val: string) => val === "audios" || val === "timeSpent",
                then: (schema) =>
                    schema
                        .required(t("valueRequired"))
                        .moreThan(0, t("valueMustBeGreaterThanZero") || "Must be greater than 0"),
                otherwise: (schema) => schema.notRequired(),
            }),
        // Time pickers if condition is selected
        conditionStartHour: Yup.date()
            .nullable()
            .when("conditionType", {
                is: "hour",
                then: (schema) => schema.required(t("startHourRequired")),
                otherwise: (schema) => schema.notRequired(),
            }),

        conditionEndHour: Yup.date()
            .nullable()
            .when(["conditionType", "conditionStartHour"], {
                is: (type: string, start: Date | null) => type === "hour" && !!start,
                then: (schema) =>
                    schema
                        .required(t("endHourRequired"))
                        .min(Yup.ref("conditionStartHour"), t("endHourAfterStartHour")),
                otherwise: (schema) => schema.notRequired(),
            }),
        // Redirection logic
        redirectionLinkType: Yup.string().when("notificationType", {
            is: "redirection",
            then: (schema) => schema.required(t("linkTypeRequired")),
            otherwise: (schema) => schema.notRequired(),
        }),
        linkValue: Yup.string().when(["notificationType", "redirectionLinkType"], ([type, linkType], schema) => {
            const shouldRequire = type === "redirection" && (linkType === "external" || linkType === "inapp");

            return shouldRequire ? schema.required(t("linkValueRequired")) : schema.notRequired();
        }),
        // Localized content
        shortTextValue: Yup.string().required(t("shortTextRequired")),
        textValue: Yup.string().required(t("textRequired")),
        contentLanguage: Yup.string().required(t("languageRequired")),
        // Optional file upload
        logoNotification: Yup.string().nullable(),
        // cityId is auto-filled when company has only one city; required when picker is shown
        cityId: Yup.string(),
    });

    // Initial form values
    const initialValues: NotificationFormValues = {
        notificationType: "urgent",
        notificationScope: "allApp",
        selectedProduct: "",
        notificationStartDate: null,
        notificationEndDate: null,
        conditionType: "",
        conditionValue: "",
        conditionStartHour: null,
        conditionEndHour: null,
        redirectionLinkType: "",
        linkValue: "",
        hotinfoLinkValue: "",
        shortTextValue: "",
        textValue: "",
        logoNotification: null,
        contentLanguage: "",
        cityId: "",
    };

    // Handle form submission
    const handleSubmit = async (
        values: NotificationFormValues,
        formikHelpers: FormikHelpers<NotificationFormValues>,
    ) => {
        const { resetForm } = formikHelpers;
        const token = localStorage.getItem("token");
        if (!token) {
            console.error("Missing token");
            return;
        }

        try {
            const isUrgent = values.notificationType === "urgent";
            const isAllApp = values.notificationScope === "allApp";
            const isSingleTour = values.notificationScope === "track";

            // Adjust start date to beginning of day (00:01 UTC) and end date to end of day (23:59 UTC)
            // Use local date components to create UTC dates to avoid timezone offset issues
            const adjustedStartDate = values.notificationStartDate
                ? (() => {
                      const d = new Date(values.notificationStartDate);
                      // Extract local date components and create UTC date
                      return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 0, 1, 0, 0)).toISOString();
                  })()
                : undefined;

            const adjustedEndDate = values.notificationEndDate
                ? (() => {
                      const d = new Date(values.notificationEndDate);
                      // Extract local date components and create UTC date
                      return new Date(
                          Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999),
                      ).toISOString();
                  })()
                : undefined;

            const payload = {
                deeplink: undefined,
                isUrgent,
                isProduct: values.redirectionLinkType === "inapp",
                allApplication: isAllApp,
                startDate: adjustedStartDate,
                endDate: adjustedEndDate,
                audioListened: values.conditionType === "audios" ? Number(values.conditionValue) : undefined,
                timeSpentInApp: values.conditionType === "timeSpent" ? Number(values.conditionValue) : undefined,
                isActive: true,
                urlNotification: values.redirectionLinkType === "external" ? values.linkValue : undefined,
                hour:
                    values.conditionType === "hour" && values.conditionStartHour && values.conditionEndHour
                        ? `${values.conditionStartHour.getHours()}:${String(
                              values.conditionStartHour.getMinutes(),
                          ).padStart(2, "0")} - ${values.conditionEndHour.getHours()}:${String(
                              values.conditionEndHour.getMinutes(),
                          ).padStart(2, "0")}`
                        : undefined,
                infoChaudeId: undefined,
                trackId: isSingleTour && values.selectedProduct ? values.selectedProduct : undefined,
                productId:
                    values.redirectionLinkType === "inapp" && values.linkValue && values.linkValue !== ""
                        ? values.linkValue
                        : undefined,
                waypointId: undefined,
                companyId: undefined,
                // cityId is sent for both global (user-picked) and track-scoped
                // (auto-derived from the selected track's city) notifications.
                cityId: values.cityId || undefined,
                localized: [
                    {
                        language: values.contentLanguage || "EN",
                        shortMessage: values.shortTextValue,
                        longMessage: values.textValue,
                    },
                ],
                logoNotification: values.logoNotification,
            };

            // Log the form values to console for debugging
            console.log("Form Submission:", {
                formValues: values,
                payload: payload,
            });
            setModalOpen(true);
            try {
                setStatus("waiting");
                const response = await createNotification({ token, payload });
                console.log("Created notification:", response);
                setStatus("success");
            } catch (err) {
                setStatus("error");
            } finally {
                // Reset form with a small delay to avoid pending updates touched fields
                setTimeout(() => {
                    resetForm();
                }, 100);
            }
        } catch (err: any) {
            console.error("Error creating notification:", err);
        }
    };

    return (
        <div className={notifyStyles.notificationPageContainer} style={{ paddingBottom: "80px" }}>
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                key={i18n.language}
            >
                {({ isSubmitting, submitForm, errors }) => (
                    <>
                        <div className={notifyStyles.notificationLayout}>
                            <div className={notifyStyles.notificationSidebar}>
                                <NotificationForm />
                            </div>
                            <div className={notifyStyles.notificationPreviewPanel}>
                                <NotificationPreview />
                            </div>
                        </div>
                        <div style={fixedButtonContainerStyle}>
                            <Tooltip
                                title={
                                    Object.keys(errors).length > 0
                                        ? t("formIncompleteTooltip", { fields: Object.values(errors).join(", ") })
                                        : ""
                                }
                                arrow
                            >
                                <span>
                                    <Button
                                        className={notifyStyles.generateButton}
                                        label={t("createNotification")}
                                        onClick={submitForm}
                                        disabled={isSubmitting || Object.keys(errors).length > 0}
                                    />
                                </span>
                            </Tooltip>
                        </div>
                    </>
                )}
            </Formik>
            <NotificationStatusModal
                isOpen={modalOpen}
                status={status}
                onClose={() => setModalOpen(false)}
                onCreateAnother={() => {
                    setModalOpen(false);
                }}
                onGoToActive={() => {
                    setModalOpen(false);
                    setActiveTab("activeNotifications");
                }}
            />
        </div>
    );
};

export default NewNotifications;
